import React, { useState } from 'react';
import Papa from 'papaparse';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { UploadSimple, FileArrowDown } from '@phosphor-icons/react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';

export const ImportAttendancePage = () => {
  const [attendanceData, setAttendanceData] = useState([]);
  const [file, setFile] = useState(null);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    setFile(selectedFile);
    Papa.parse(selectedFile, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        const parsed = results.data.map((row) => ({
          emp_id: row['Emp ID'] || row['emp_id'] || row['Employee ID'] || '',
          name: row['Name'] || row['Employee Name'] || row['name'] || '',
          actual_days: parseInt(row['Actual Days'] || row['actual_days'] || row['Days'] || '30', 10),
          days_worked:
            parseInt(row['Days Worked'] || row['days_worked'] || row['N.Days'] || row['Present'] || '30', 10),
        }));
        setAttendanceData(parsed.filter((d) => d.emp_id));
      },
      error: (error) => {
        console.error('CSV parsing error:', error);
        alert('Error parsing CSV file. Please check the format.');
      },
    });
  };

  const downloadTemplate = () => {
    const csv = 'Emp ID,Name,Actual Days,Days Worked\nEMP001,John Doe,30,28\nEMP002,Jane Smith,30,30';
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'attendance_template.csv';
    a.click();
  };

  return (
    <div data-testid="import-attendance-page">
      <div className="mb-6">
        <h1 className="text-2xl font-bold tracking-tight mb-1" style={{ color: 'var(--dx-text)' }}>
          Import Attendance
        </h1>
        <p className="text-sm" style={{ color: 'var(--dx-text2)' }}>
          Upload a CSV exported from Google Sheets — emp ID, name, actual days, days worked.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        <Card className="p-5 dx-card lg:col-span-2">
          <h3 className="text-base font-semibold mb-3" style={{ color: 'var(--dx-text)' }}>Upload CSV</h3>
          <div
            className="border-2 border-dashed rounded-lg p-8 text-center transition-all"
            style={{ borderColor: 'var(--dx-border2)' }}
          >
            <UploadSimple size={32} className="mx-auto mb-3" style={{ color: 'var(--dx-text3)' }} />
            <p className="text-sm mb-4" style={{ color: 'var(--dx-text2)' }}>
              Select a CSV file with columns: Emp ID, Name, Actual Days, Days Worked
            </p>
            <input
              type="file"
              accept=".csv"
              onChange={handleFileChange}
              className="hidden"
              id="csv-upload"
              data-testid="csv-file-input"
            />
            <label htmlFor="csv-upload">
              <Button type="button" asChild style={{ background: 'var(--dx-primary)' }} className="text-white hover:opacity-90">
                <span data-testid="select-file-btn">Select File</span>
              </Button>
            </label>
            {file && (
              <p className="mt-3 text-xs font-medium" style={{ color: 'var(--dx-primary)' }}>
                Selected: {file.name}
              </p>
            )}
          </div>
        </Card>

        <Card className="p-5 dx-card">
          <h3 className="text-base font-semibold mb-3" style={{ color: 'var(--dx-text)' }}>CSV Format</h3>
          <ul className="list-disc list-inside text-sm space-y-1 mb-3" style={{ color: 'var(--dx-text2)' }}>
            <li>Emp ID</li>
            <li>Name</li>
            <li>Actual Days</li>
            <li>Days Worked</li>
          </ul>
          <Button
            onClick={downloadTemplate}
            variant="outline"
            className="w-full"
            data-testid="download-template-btn"
          >
            <FileArrowDown size={16} className="mr-2" />
            Download Template
          </Button>
        </Card>
      </div>

      {attendanceData.length > 0 && (
        <Card className="dx-card">
          <div
            className="p-4 flex items-center justify-between"
            style={{ borderBottom: '1px solid var(--dx-border)' }}
          >
            <h3 className="text-base font-semibold" style={{ color: 'var(--dx-text)' }}>
              Preview ({attendanceData.length} records)
            </h3>
            <Button
              onClick={() => {
                localStorage.setItem('attendance_data', JSON.stringify(attendanceData));
                window.location.href = '/payroll';
              }}
              style={{ background: 'var(--dx-primary)' }}
              className="text-white hover:opacity-90"
              data-testid="proceed-to-payroll-btn"
            >
              Proceed to Payroll
            </Button>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Emp ID</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Actual Days</TableHead>
                <TableHead>Days Worked</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {attendanceData.map((row, idx) => (
                <TableRow key={idx} data-testid={`attendance-row-${idx}`}>
                  <TableCell className="font-medium">{row.emp_id}</TableCell>
                  <TableCell>{row.name}</TableCell>
                  <TableCell>{row.actual_days}</TableCell>
                  <TableCell>{row.days_worked}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}
    </div>
  );
};

export default ImportAttendancePage;
